import { Component, computed, EventEmitter, inject, input, LOCALE_ID, Output, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ReactiveFormsModule } from "@angular/forms";
import { CloudProjectOdxTableService } from "@components/cloud-project-odx-tables/cloud-project-odx-table.service";
import { CloudProjectMetadata } from "@domain/project/cloud-project-metadata";
import { ProjectStatusIconMapperService } from "@domain/project/project-status-icon-mapper.service";
import { ProjectStatusService } from "@domain/project/project-status.service";
import { ProjectStatusDto } from "@domain/project/template/property/project/details/information/project-status.dto";
import { UserService } from "@domain/user/user.service";
import { CoreModule } from "@odx/angular";
import { DynamicTextContent } from "@odx/angular/cdk/dynamic-view";
import { ChipComponent } from "@odx/angular/components/chip";
import { FormFieldModule } from "@odx/angular/components/form-field";
import { IconComponent } from "@odx/angular/components/icon";
import { PageChangeEvent, PaginatorModule } from "@odx/angular/components/paginator";
import { SortStatus, TableComponent, TableHeaderCell, TableModule, TableSortVariant } from "@odx/angular/components/table";
import { TooltipDirective } from "@odx/angular/components/tooltip";
import { Observable, shareReplay, switchMap } from "rxjs";

interface TableData {
  displayName: string;
  lastModifiedOn: string;
  projectStatus: string;
  icon: string;
  cloudProjectMetadata: CloudProjectMetadata;
}

@Component({
  selector: "app-delete-cloud-project-odx-table",
  imports: [
    CoreModule,
    TableModule,
    ReactiveFormsModule,
    FormFieldModule,
    TableComponent,
    PaginatorModule,
    IconComponent,
    TooltipDirective,
    ChipComponent,
  ],
  templateUrl: "./delete-cloud-project-odx-table.component.html",
  styleUrl: "./delete-cloud-project-odx-table.component.scss",
})
export class DeleteCloudProjectOdxTableComponent {
  private localeId = inject(LOCALE_ID);
  private projectStatusIconMapperService: ProjectStatusIconMapperService = inject(ProjectStatusIconMapperService);
  private projectStatusService: ProjectStatusService = inject(ProjectStatusService);
  private tableService = inject(CloudProjectOdxTableService);
  private userService = inject(UserService);

  protected selected?: CloudProjectMetadata;

  protected total = 0;
  protected pageIndex = 0;
  protected readonly tableSize = 10;

  protected readonly headerData: TableHeaderCell[] = [
    { name: "displayName", title: $localize`:@@cloudProjectsTable.column1.header:Projektname`, sortable: true, filter: true },
    {
      name: "lastModifiedOn",
      title: $localize`:@@cloudProjectsTable.column2.header:Zuletzt synchronisiert`,
      sortable: true,
      filter: true,
    },
    {
      name: "projectStatus",
      title: $localize`:@@cloudProjectsTable.header.projectStatus:Projektstatus`,
      filter: true,
    },
    { name: "cloudProjectMetadata", title: "", sortable: false, filter: false }, // Workaround to display no header above the last column as otherwise odx automatically generates the header.
  ];
  readonly cloudProjectMetadata = input<CloudProjectMetadata[]>([]);

  readonly sortParams = signal<SortStatus>({
    column: "lastModifiedOn",
    sortVariant: TableSortVariant.DESC,
  });
  readonly filterParams = signal<Record<string, string>>({});
  readonly paginationParams = signal({ startIndex: 0, endIndex: this.tableSize });

  private statuses$: Observable<ProjectStatusDto[]> = this.userService.businessScope$.pipe(
    switchMap((scope) => this.projectStatusService.getProjectStatuses(scope)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly statuses = toSignal<ProjectStatusDto[] | undefined>(this.statuses$, {
    initialValue: undefined,
  });

  totalData = computed(() => {
    const statuses = this.statuses();
    const metadata = this.cloudProjectMetadata();
    if (!statuses || !metadata) {
      return [];
    }
    try {
      return this.cloudProjectMetadata().map((metadata) => ({
        displayName: metadata.displayName,
        customerName: metadata.customerName,
        lastModifiedOn: metadata.lastModifiedOn,
        projectStatus: metadata.status,
        icon: this.projectStatusIconMapperService.mapIcon(metadata.status, statuses),
        cloudProjectMetadata: metadata,
      }));
    } catch (e) {
      console.error("totalData computed error:", e);
      return [];
    }
  });

  protected dataToDisplay = computed<TableData[]>(() => this.generateDataToDisplay());

  @Output() projectSelected = new EventEmitter<CloudProjectMetadata>();

  constructor() {}
  public sorted(sortParams: SortStatus): void {
    this.sortParams.update(() => sortParams);
  }

  public filter(filterParams: Record<string, string>): void {
    this.pageIndex = 0;
    this.filterParams.update(() => filterParams);
    this.paginationParams.update((p) => ({
      ...p,
      startIndex: 0,
      endIndex: this.tableSize,
    }));
  }

  public pageChanged(event: PageChangeEvent): void {
    this.pageIndex = event.pageIndex;
    this.paginationParams.update((p) => ({
      ...p,
      startIndex: event.pageIndex * this.tableSize,
      endIndex: (event.pageIndex + 1) * this.tableSize,
    }));
  }

  protected onClickIcon(cloudProjectMetadata: CloudProjectMetadata) {
    this.projectSelected.emit(cloudProjectMetadata);
  }

  protected getSummaryContentFn = (startItemIndex: number, endItemIndex: number, length: number): DynamicTextContent => {
    return this.tableService.getSummaryContentFn(startItemIndex, endItemIndex, length);
  };

  protected getRangeContentFn = (pageIndex: number, totalPages: number): DynamicTextContent => {
    return this.tableService.getRangeContentFn(pageIndex, totalPages);
  };

  private generateDataToDisplay(): TableData[] {
    const rows = this.totalData();
    const sorted = this.tableService.sortData<TableData>(this.sortParams(), rows);
    const converted = this.tableService.convertLastModifiedOn<TableData>(sorted);
    const filtered = this.tableService.filterData(this.filterParams(), converted);
    this.total = filtered.length;

    const { startIndex, endIndex } = this.paginationParams();
    return filtered.slice(startIndex, endIndex);
  }

  protected convertTimestampToDate(timestamp: string) {
    return this.tableService.convertTimestamp(timestamp, this.localeId);
  }
}
